<template>
  <div class="z-0 mt-3 flex flex-1 h-full max-w-full overflow-y-auto overflow-x-hidden bg-light-screen-background dark:bg-dark-screen-background">
    <div class="flex flex-row w-full h-full max-w-full mx-auto ml-4">
      <div class="mr-4 mb-6 w-full h-full mx-auto overflow-visible">
        <!-- Header Section -->
        <div class="flex w-full items-center justify-between mb-6">
          <div>
            <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">
              API Keys
            </h1>
            <p class="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Manage API keys for your organization. Do not share your secret API keys.
            </p>
          </div>
          <button
            class="btn-primary w-fit"
            @click="openCreateModal">
            <Plus class="w-6 h-6 mr-2" />
            Create new secret key
          </button>
        </div>

        <!-- API Keys Table -->
        <div class="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-lg">
          <div v-if="loading" class="flex justify-center py-8">
            <LoadingSpinner class="w-8 h-8" />
          </div>
          
          <div v-else-if="apiKeys.length === 0" class="text-center py-12">
            <Key class="mx-auto h-12 w-12 text-gray-400" />
            <h3 class="mt-2 text-sm font-medium text-gray-900 dark:text-white">
              No API keys
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              Get started by creating your first secret key.
            </p>
          </div>

          <div v-else class="overflow-x-auto">
            <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-600">
              <thead class="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Name
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Secret Key
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Scopes
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Last Used
                  </th>
                  <th class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Created
                  </th>
                  <th class="relative px-6 py-3">
                    <span class="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                <tr v-for="apiKey in apiKeys" :key="apiKey.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td class="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {{ apiKey.name }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm">
                    <code class="text-gray-600 dark:text-gray-300">
                      {{ formatMaskedKey(apiKey.partial_key) }}
                    </code>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    <span v-if="getPermissionSummary(apiKey.scopes) === 'All'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300">All</span>
                    <span v-else-if="getPermissionSummary(apiKey.scopes) === 'Read-only'" class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">Read-only</span>
                    <span v-else class="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">Custom</span>
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {{ apiKey.last_use ? formatDate(apiKey.last_use) : 'Never' }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                    {{ formatDate(apiKey.created) }}
                  </td>
                  <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div class="flex items-center justify-end space-x-2">
                      <button class="text-gray-400 hover:text-blue-600 dark:hover:text-blue-400" title="Edit" @click="editApiKey(apiKey)"><Edit class="w-5 h-5" /></button>
                      <button class="text-gray-400 hover:text-red-600 dark:hover:text-red-400" title="Revoke" @click="openRevokeModal(apiKey)"><Trash2 class="w-5 h-5" /></button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Revoke API Key Modal -->
        <div v-if="showRevokeConfirmModal" class="fixed inset-0 z-50 overflow-y-auto">
          <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeRevokeModal" />
            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" />

            <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div class="sm:flex sm:items-start">
                <div class="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900 sm:mx-0 sm:h-10 sm:w-10">
                  <Trash2 class="h-6 w-6 text-red-600 dark:text-red-400" />
                </div>
                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Revoke API Key
                  </h3>
                  <div class="mt-2">
                    <p class="text-sm text-gray-500 dark:text-gray-400">
                      Are you sure you want to revoke the key "{{ keyToRevoke.name }}"? This action is permanent and cannot be undone.
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  class="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
                  @click="confirmRevoke">
                  Revoke
                </button>
                <button
                  type="button"
                  class="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                  @click="closeRevokeModal">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Create/Edit API Key Modal -->
        <div v-if="showModal" class="fixed inset-0 z-50 overflow-y-auto">
          <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" @click="closeModal" />
            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" />

            <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6">
              <h3 class="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-6">
                {{ isEditMode ? 'Edit secret key' : 'Create new secret key' }}
              </h3>
              
              <form @submit.prevent="isEditMode ? updateApiKey() : createApiKey()">
                <div class="space-y-6">
                  <!-- Owned by -->
                  <!--
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Owned by</label>
                    <div class="mt-2 btn-group">
                      <button type="button" :class="{'btn-group-active': !newApiKey.is_service_account}" @click="newApiKey.is_service_account = false">Personal</button>
                      <button type="button" :class="{'btn-group-active': newApiKey.is_service_account}" @click="newApiKey.is_service_account = true">Service account</button>
                    </div>
                    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      <span v-if="!newApiKey.is_service_account">This API key is tied to your user account.</span>
                      <span v-else>A new bot member will be created for this key.</span>
                    </p>
                  </div>
                  -->

                  <!-- Name -->
                  <div>
                    <label for="key-name" class="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Name <span class="text-gray-400 font-normal">(Optional)</span>
                    </label>
                    <input id="key-name"
                           v-model="newApiKey.name"
                           type="text"
                           class="form-input w-full mt-1"
                           :class="{ 'border-red-500': serverErrors && serverErrors.name }"
                           placeholder="e.g., My Test Key">
                    <p v-if="serverErrors && serverErrors.name" class="mt-2 text-sm text-red-600 dark:text-red-400">
                      {{ serverErrors.name[0] }}
                    </p>
                  </div>

                  <!-- Permissions -->
                  <div>
                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300">Permissions</label>
                    <div class="mt-2 btn-group">
                      <button type="button" :class="{'btn-group-active': permissionMode === 'all'}" @click="setPermissionMode('all')">All</button>
                      <button type="button" :class="{'btn-group-active': permissionMode === 'readonly'}" @click="setPermissionMode('readonly')">Read-only</button>
                      <button type="button" :class="{'btn-group-active': permissionMode === 'custom'}" @click="setPermissionMode('custom')">Custom</button>
                    </div>
                  </div>

                  <!-- Resources (for Custom permissions) -->
                  <div v-if="permissionMode === 'custom'" class="space-y-4 border-t border-gray-200 dark:border-gray-700 pt-4">
                    <div v-for="(resources, category) in categorizedResources" :key="category" class="pb-4 mb-4 border-b border-gray-200 dark:border-gray-700 last:border-b-0">
                      <h3 class="text-md font-semibold text-gray-800 dark:text-gray-200 mb-4">
                        {{ category }}
                      </h3>
                      <div v-for="resource in resources" :key="resource.name" class="group flex items-center justify-between ml-4 mb-4">
                        <div class="flex items-center">
                          <div>
                            <h4 class="text-sm font-medium text-gray-800 dark:text-gray-200">
                              {{ resource.name }}
                              <Popper
                                v-if="resource.endpoints && resource.endpoints.length"
                                arrow
                                offset-distance="2"
                                placement="right"
                                class="ml-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-150"
                                @click.stop>
                                <template #content="{ close }">
                                  <div class="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 max-w-xs">
                                    <div class="flex justify-between items-center mb-2">
                                      <h5 class="font-semibold text-gray-800 dark:text-gray-200">
                                        Included Endpoints
                                      </h5>
                                      <button class="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300" @click="close">
                                        <X class="w-4 h-4" />
                                      </button>
                                    </div>
                                    <ul class="list-disc list-inside text-xs text-gray-600 dark:text-gray-400 max-h-48 overflow-y-auto">
                                      <li v-for="endpoint in resource.endpoints" :key="endpoint">
                                        <code>{{ endpoint }}</code>
                                      </li>
                                    </ul>
                                  </div>
                                </template>
                                <Info class="w-4 h-4 text-gray-400 hover:text-blue-500 cursor-pointer" />
                              </Popper>
                            </h4>
                            <p class="text-xs text-gray-500 dark:text-gray-400">
                              {{ resource.description }}
                            </p>
                          </div>
                        </div>
                        <div class="btn-group">
                          <button type="button" :class="{'btn-group-active': getResourcePermission(resource) === 'none'}" @click="setResourcePermission(resource, 'none')">None</button>
                          <button v-if="resource.permissions_to_scopes.read" type="button" :class="{'btn-group-active': getResourcePermission(resource) === 'read'}" @click="setResourcePermission(resource, 'read')">Read</button>
                          <button v-if="resource.permissions_to_scopes.write" type="button" :class="{'btn-group-active': getResourcePermission(resource) === 'write'}" @click="setResourcePermission(resource, 'write')">Read/Write</button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Modal Actions -->
                <div class="mt-8 flex justify-end space-x-3">
                  <button type="button" class="btn-cancel" @click="closeModal">Cancel</button>
                  <button type="submit" class="btn-primary w-fit" :disabled="isSubmitting || !isFormValid">
                    <LoadingSpinner v-if="isSubmitting" class="w-4 h-4 mr-2" />
                    {{ isEditMode ? 'Save changes' : 'Create secret key' }}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
        
        <!-- Success Modal for New Key -->
        <div v-if="showSuccessModal && newlyCreatedKey" class="fixed inset-0 z-50 overflow-y-auto">
          <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div class="fixed inset-0 bg-gray-900 bg-opacity-60 transition-opacity" @click="closeSuccessModal" />

            <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true" />

            <div class="inline-block align-bottom bg-white dark:bg-gray-800 rounded-2xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-md sm:w-full sm:p-8">
              <div class="flex items-center justify-center w-16 h-16 mx-auto bg-green-100 dark:bg-green-900/50 rounded-full mb-6">
                <CheckCircle class="w-10 h-10 text-green-600 dark:text-green-400" />
              </div>
              
              <div class="text-center mb-6">
                <h3 class="text-2xl font-bold text-gray-900 dark:text-white">
                  Secret Key Created
                </h3>
                <p class="mt-3 text-base text-gray-500 dark:text-gray-400">
                  For security reasons, we can't show this key again. Please copy and store it securely.
                </p>
              </div>

              <div class="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4 mb-8">
                <div class="flex items-center justify-between">
                  <code class="flex-1 text-sm font-mono text-gray-800 dark:text-gray-200 truncate pr-4">
                    {{ newlyCreatedKey.sensitive_id }}
                  </code>
                  <button
                    class="flex items-center space-x-2 px-4 py-2 rounded-md transition-colors duration-200"
                    :class="isCopied ? 'bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300' : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300'"
                    @click="copyToClipboard(newlyCreatedKey.sensitive_id)">
                    <Check v-if="isCopied" class="h-5 w-5" />
                    <Copy v-else class="h-5 w-5" />
                    <span class="text-sm font-medium">{{ isCopied ? 'Copied!' : 'Copy' }}</span>
                  </button>
                </div>
              </div>
              
              <div class="flex justify-center">
                <button
                  class="btn-primary w-full"
                  @click="closeSuccessModal">
                  Done
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, reactive, computed, watch } from 'vue'
import { Plus, Key, Copy, Edit, Trash2, Check, CheckCircle, Info, X } from 'lucide-vue-next'
import Popper from 'vue3-popper'
import LoadingSpinner from '@/components/shared/LoadingSpinner.vue'
import apiKeysService from '@/services/api_keys.service'

export default {
  name: 'ApiKeysSimple',
  components: { Plus, Key, Copy, Edit, Check, Trash2, CheckCircle, LoadingSpinner, Popper, Info, X },
  setup() {
    const apiKeys = ref([])
    const apiResources = ref([])
    const allScopes = ref([])
    const loading = ref(false)
    const isCopied = ref(false)
    const showModal = ref(false)
    const isEditMode = ref(false)
    const showSuccessModal = ref(false)
    const isSubmitting = ref(false)
    const newlyCreatedKey = ref(null)
    const permissionMode = ref('all') 
    const serverErrors = ref(null)
    const showRevokeConfirmModal = ref(false)
    const keyToRevoke = ref(null)

    const newApiKey = reactive({
      id: null,
      name: '',
      is_service_account: false,
      scopes: [],
    })

    const allReadScopes = computed(() => allScopes.value.filter(s => s.scope.endsWith('.read')).map(s => s.scope))
    
    const categorizedResources = computed(() => {
      if (!apiResources.value.length || !allScopes.value.length) {
        return {}
      }

      const categories = {}
      const scopeMap = new Map(allScopes.value.map(s => [s.scope, s.category]))

      apiResources.value.forEach(resource => {
        const readScopes = resource.permissions_to_scopes.read || []
        const writeScopes = resource.permissions_to_scopes.write || []
        const firstScope = readScopes.length > 0 ? readScopes[0] : writeScopes[0]
        
        if (firstScope) {
          const category = scopeMap.get(firstScope) || 'Other'
          if (!categories[category]) {
            categories[category] = []
          }
          categories[category].push(resource)
        }
      })

      return categories
    })

    // Computed property to check if form is valid for submission
    const isFormValid = computed(() => {
      if (permissionMode.value === 'custom' && newApiKey.scopes.length === 0) {
        return false
      }
      return true
    })
    
    // Watch for changes in the scopes array to update the permission mode button
    watch(() => [...newApiKey.scopes], (newScopes) => {
      // Don't auto-change permission mode if user manually selected custom
      if (permissionMode.value === 'custom') {
        return
      }
      
      const newScopesSet = new Set(newScopes)
      const allScopesSet = new Set(allScopes.value.map(s => s.scope))
      const allReadScopesSet = new Set(allReadScopes.value)

      if (newScopes.length === 0) {
        permissionMode.value = 'all'
      } else if (allScopes.value.length > 0 && newScopesSet.size === allScopesSet.size && [...allScopesSet].every(s => newScopesSet.has(s))) {
        permissionMode.value = 'all'
      } else if (newScopesSet.size === allReadScopesSet.size && [...allReadScopesSet].every(s => newScopesSet.has(s))) {
        permissionMode.value = 'readonly'
      } else {
        permissionMode.value = 'custom'
      }
    })

    const loadApiDefinitions = async () => {
      try {
        const [scopesRes, resourcesRes] = await Promise.all([
          apiKeysService.getAvailableScopes(),
          apiKeysService.getApiResources(),
        ])
        allScopes.value = scopesRes.scopes
        apiResources.value = resourcesRes.resources
      } catch (error) {
        console.error('Failed to load API definitions', error)
      }
    }

    const loadApiKeys = async () => {
      loading.value = true
      try {
        const response = await apiKeysService.listApiKeys()
        apiKeys.value = response.results || response || []
      } catch (error) {
        console.error('Error loading API keys:', error)
      } finally {
        loading.value = false
      }
    }

    const resetForm = () => {
      newApiKey.id = null
      newApiKey.name = ''
      newApiKey.is_service_account = false
      newApiKey.scopes = []
      permissionMode.value = 'all'
      serverErrors.value = null
    }
    
    const openCreateModal = () => {
      resetForm()
      isEditMode.value = false
      showModal.value = true
    }

    const editApiKey = (key) => {
      resetForm()
      newApiKey.id = key.id
      newApiKey.name = key.name
      newApiKey.is_service_account = key.is_service_account
      newApiKey.scopes = [...key.scopes]
      isEditMode.value = true
      showModal.value = true
    }

    const closeModal = () => {
      showModal.value = false
      resetForm()
    }

    const createApiKey = async () => {
      isSubmitting.value = true
      serverErrors.value = null
      try {
        const payload = { name: newApiKey.name, is_service_account: newApiKey.is_service_account, scopes: newApiKey.scopes }
        const response = await apiKeysService.createApiKey(payload)
        newlyCreatedKey.value = response
        apiKeys.value.unshift({ ...response, scopes: newApiKey.scopes })
        closeModal()
        showSuccessModal.value = true
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          serverErrors.value = error.response.data.errors
        } else {
          console.error('Error creating API key:', error)
        }
      } finally {
        isSubmitting.value = false
      }
    }
    
    const updateApiKey = async () => {
      isSubmitting.value = true
      serverErrors.value = null
      try {
        const payload = { name: newApiKey.name, scopes: newApiKey.scopes }
        const response = await apiKeysService.updateApiKey(newApiKey.id, payload)
        const index = apiKeys.value.findIndex(k => k.id === newApiKey.id)
        if (index !== -1) {apiKeys.value[index] = { ...apiKeys.value[index], ...response }}
        closeModal()
      } catch (error) {
        if (error.response && error.response.data && error.response.data.errors) {
          serverErrors.value = error.response.data.errors
        } else {
          console.error('Error updating API key:', error)
        }
      } finally {
        isSubmitting.value = false
      }
    }

    const openRevokeModal = (apiKey) => {
      keyToRevoke.value = apiKey
      showRevokeConfirmModal.value = true
    }

    const closeRevokeModal = () => {
      keyToRevoke.value = null
      showRevokeConfirmModal.value = false
    }

    const confirmRevoke = async () => {
      if (!keyToRevoke.value) {return}
      try {
        await apiKeysService.revokeApiKey(keyToRevoke.value.id)
        apiKeys.value = apiKeys.value.filter(k => k.id !== keyToRevoke.value.id)
        if (window.$toast) {
          window.$toast.success(`API key "${keyToRevoke.value.name}" has been revoked.`)
        }
      } catch (error) {
        console.error('Error revoking API key:', error)
        if (window.$toast) {
          window.$toast.error('Failed to revoke API key.')
        }
      } finally {
        closeRevokeModal()
      }
    }
    
    const setPermissionMode = (mode) => {
      permissionMode.value = mode
      if (mode === 'all') {
        newApiKey.scopes = []
      } else if (mode === 'readonly') {
        newApiKey.scopes = [...allReadScopes.value]
      } else if (mode === 'custom' && newApiKey.scopes.length > 0) {
        // Do nothing, keep custom scopes
      } else if (mode === 'custom') {
        newApiKey.scopes = [] // Default to none selected for custom
      }
    }

    const getResourcePermission = (resource) => {
      if (permissionMode.value === 'all') {
        return resource.permissions_to_scopes.write ? 'write' : 'read'
      }
      const scopes = new Set(newApiKey.scopes)
      const writeScopes = resource.permissions_to_scopes.write || []
      const readScopes = resource.permissions_to_scopes.read || []
      
      const hasWrite = writeScopes.every(s => scopes.has(s))
      if (hasWrite) {return 'write'}

      const hasRead = readScopes.every(s => scopes.has(s))
      if (hasRead) {return 'read'}
      
      return 'none'
    }
    
    const setResourcePermission = (resource, level) => {
      const currentScopes = new Set(newApiKey.scopes)
      const writeScopes = resource.permissions_to_scopes.write || []
      const readScopes = resource.permissions_to_scopes.read || []
      
      // Remove all scopes for this resource first
      ;[...readScopes, ...writeScopes].forEach(s => currentScopes.delete(s))
      
      if (level === 'read') {
        readScopes.forEach(s => currentScopes.add(s))
      } else if (level === 'write') {
        // Read/Write implies read as well
        readScopes.forEach(s => currentScopes.add(s))
        writeScopes.forEach(s => currentScopes.add(s))
      }
      
      newApiKey.scopes = Array.from(currentScopes)
    }

    const getPermissionSummary = (scopes) => {
      if (!scopes || scopes.length === 0) { return 'All' }
      const scopeSet = new Set(scopes)
      if (allScopes.value.length > 0 && scopeSet.size === allScopes.value.map(s => s.scope).length) {return 'All'}
      
      const readOnlySet = new Set(allReadScopes.value)
      if (scopeSet.size === readOnlySet.size && [...scopeSet].every(s => readOnlySet.has(s))) {
        return 'Read-only'
      }
      return 'Custom'
    }

        const copyToClipboard = async (text) => {
      try {
        await navigator.clipboard.writeText(text)
        isCopied.value = true
        setTimeout(() => {
          isCopied.value = false
        }, 2000) // Reset after 2 seconds
        if (window.$toast) {window.$toast.success('Copied to clipboard')}
      } catch (error) {
        console.error('Error copying to clipboard:', error)
        if (window.$toast) {window.$toast.error('Failed to copy to clipboard')}
      }
    }

    const closeSuccessModal = () => {
      showSuccessModal.value = false
      newlyCreatedKey.value = null
    }

    const formatMaskedKey = (key) => key ? `sk-...${key.slice(-4)}` : ''
    const formatDate = (dateString) => dateString ? new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : 'Never'

    onMounted(() => {
      loadApiKeys()
      loadApiDefinitions()
    })

    return {
      apiKeys,isCopied, loading, showModal, isEditMode, isSubmitting, newApiKey, permissionMode, newlyCreatedKey, showSuccessModal, isFormValid, categorizedResources, serverErrors, showRevokeConfirmModal, keyToRevoke,
      openCreateModal, editApiKey, closeModal, createApiKey, updateApiKey, openRevokeModal, closeRevokeModal, confirmRevoke,
      setPermissionMode, getResourcePermission, setResourcePermission, getPermissionSummary,
      formatMaskedKey, formatDate,copyToClipboard, closeSuccessModal
    }
  }
}
</script>

<style scoped>
/* A simple button group style inspired by the screenshots */
.btn-group {
  @apply inline-flex rounded-md shadow-sm;
}
.btn-group button {
  @apply relative inline-flex items-center px-3 py-1.5 border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500;
  @apply dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600;
}
.btn-group button:first-child {
  @apply rounded-l-md;
}
.btn-group button:last-child {
  @apply rounded-r-md;
}
.btn-group button:not(:first-child) {
  @apply -ml-px;
}
.btn-group .btn-group-active {
  @apply bg-blue-50 border-blue-500 text-blue-700 z-10;
  @apply dark:bg-blue-900/50 dark:border-blue-500 dark:text-white;
}
</style>
